import values from "object.values";
import moment from "moment";
import momentTz from "moment-timezone";

import { cloneDeep } from "lodash";
import isUrl from "is-url";
import {
  isoCountries,
  filterValues,
  ethnicities,
  isoLanguages,
  licensingDurations,
  licensingTableDurations,
} from "./constants";

const regExpEmail =
  /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
const regExpAlphaNumeric = /^[a-z0-9]+$/i;

export const calculateRemainingTime = (endDate) => {
  const now = new Date();
  const end = new Date(endDate);
  const timeDiff = end - now;
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  if (days > 365) {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    const months = Math.floor(remainingDays / 30);
    const remainingDaysInMonth = remainingDays % 30;
    return `Only ${years} year${years !== 1 ? "s" : ""} ${
      months > 0 ? months + " month" + (months !== 1 ? "s" : "") : ""
    } ${
      remainingDaysInMonth > 0
        ? remainingDaysInMonth +
          " day" +
          (remainingDaysInMonth !== 1 ? "s" : "")
        : ""
    } left`;
  } else if (days > 30) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `Only ${months} month${months !== 1 ? "s" : ""} ${
      remainingDays > 0
        ? remainingDays + " day" + (remainingDays !== 1 ? "s" : "")
        : ""
    } left`;
  } else if (days > 0) {
    return `Only ${days} day${days !== 1 ? "s" : ""} left`;
  } else if (hours > 0) {
    return `Only ${hours} hour${hours !== 1 ? "s" : ""} left`;
  } else if (minutes > 0) {
    return `Only ${minutes} minute${minutes !== 1 ? "s" : ""} left`;
  } else if (seconds > 0) {
    return `Only ${seconds} second${seconds !== 1 ? "s" : ""} left`;
  } else {
    return "";
  }
};

if (!Object.values) values.shim();

export const mountingComponentShouldFetch = (loadedFor, currentId) =>
  loadedFor !== currentId;

export const validEmail = (email) => regExpEmail.test(email);

export const validAlphaNumeric = (stringCheck) =>
  regExpAlphaNumeric.test(stringCheck);

export const validUrl = (url) => isUrl(url);

export const dateString = (date) => moment.utc(date).format("MMMM Do, YYYY");

export const shortDateString = (date) => moment(date).format("MM/DD/YYYY");

export const videoTopics = ({
  tags,
  location,
  context,
  video_format,
  mturk_topics_tags,
}) =>
  [tags, location, context, video_format, mturk_topics_tags]
    .join(" ")
    .split(/[\s,#]+/)
    .filter((topic) => topic)
    .join(", ");

export const videosTopics = (videos) =>
  videos
    .reduce(
      (topics, video) => topics.concat(videoTopics(video).split(", ")),
      []
    )
    .filter((topic, i, topics) => topic && topics.indexOf(topic) === i)
    .sort()
    .join(", ");

export const deleteValue = (array, value) => {
  const i = array.indexOf(value);
  if (i > -1) array.splice(i, 1);
};

export const numString = (string) => {
  if (string) {
    const num = parseInt(string);
    return num ? num.toLocaleString() : "0";
  }
  return "0";
};

export const find = (items, id, key = "_id") => {
  const array = items ? items.filter((item) => item) : [];
  const result =
    array && array.length ? array.find((item) => item[key] === id) : {};
  return result || {};
};

export const findProduct = (products, id) => {
  if (products && products.length) {
    return products.find(({ _id, name }) => _id === id || name === id) || null;
  }
  return null;
};

export const timeString = (string) =>
  string ? string.replace(/PT(.*M)?(.*S)?/, "$1 $2") : "";

export const secToHHMMSS = (totalSeconds) => {
  if (isNaN(totalSeconds)) {
    return totalSeconds;
  }
  totalSeconds = Math.round(totalSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? `0${v}` : v))
    .filter((v, i) => v !== "00" || i > 0)
    .join(":");
};

export const timeInSeconds = (time) => {
  if (isNaN(time)) {
    const hms = time?.split(":");
    if (hms && hms?.length === 3) {
      return hms[0] * 3600 + hms[1] * 60 + hms[2] * 1;
    } else {
      return 0;
    }
  }
  return time;
};

export const countOption = (value, label) => `${numString(value)}+ ${label}`;

export const filtersArray = (filters) => {
  // DO: Fix re render push to array duplicated items (dismissFilter)
  const array = [];
  Object.keys(filters).forEach((key) => {
    if (key === "topic") {
      const { subTopics, topics } = filters[key];
      if (subTopics && subTopics.length) {
        subTopics.forEach((value) =>
          array.push({ key, value, subKey: "subTopics" })
        );
      }
      if (topics && topics.length) {
        topics.forEach((value) => array.push({ key, value, subKey: "topics" }));
      }
    } else if (filters[key].radio) {
      if (filters[key].value) {
        let { value } = filters[key];
        value = isNaN(value) ? value : countOption(value, key);
        array.push({ key, value });
      }
    } else {
      Array.isArray(filters[key])
        ? filters[key].forEach((value) => array.push({ key, value }))
        : Object.keys(filters[key]).forEach((subKey) => {
            filters[key][subKey].forEach((value) =>
              array.push({ key, subKey, value })
            );
          });
    }
  });
  return array;
};

export const countryName = (code) => isoCountries[code] || code;

export const sortAgeRanges = (data) =>
  data
    .filter((age) => age._id)
    .sort((a, b) => a._id.match(/[0-9]+/) - b._id.match(/[0-9]+/));

export const productImage = (product, pictures) => {
  const { image, _id: id } = product;
  const picturesWithUrl = pictures.filter(
    ({ profile_photo }) => profile_photo && profile_photo.url
  );
  const picture = find(picturesWithUrl, id, "imageable_id").profile_photo;
  if (picture) {
    return picture.url;
  }
  if (!picture && image) {
    return image?.large_url;
  }
  return "";
};

export const productImageHandler = (product, pictures) => {
  const { image, _id: id } = product;
  if (image) {
    return image.largeUrl;
  }
  const picturesWithUrl = pictures.filter(
    ({ profilePhoto }) => profilePhoto && profilePhoto.url
  );
  const picture = find(picturesWithUrl, id, "imageable_id").profilePhoto;
  if (picture) {
    return picture.url;
  }
  return "";
};

export const getCompanyNameIfTryAndBuy = (brandName) => {
  if (typeof brandName === "string") {
    const index = brandName.indexOf("_TryAndBuy_");
    if (index !== -1) {
      const name = brandName.substr(0, index);
      return name.replace("_", " ");
    }
  }
  return brandName;
};

export const options = (array) =>
  Array.isArray(array)
    ? array.map((value) => ({ value, label: getCompanyNameIfTryAndBuy(value) }))
    : [];

export const createOptions = options;

export const countOptions = (array, label) =>
  array.map((value) => ({
    value,
    label: countOption(value, label),
  }));

export const countryOptions = (countries) =>
  countries
    .filter((code) => code)
    .filter((code) => isoCountries[code])
    .sort((a, b) => (isoCountries[a] > isoCountries[b] ? 1 : -1))
    .map((code) => ({ value: code, label: isoCountries[code] }));

export const ethnicityOptions = () =>
  ethnicities.map((ethnicity) => ({
    value: ethnicity,
    label: filterValues[ethnicity],
  }));

export const plural = (string) => {
  const lastLetter = string.slice(-1);
  if (lastLetter === "s") return string;
  if (lastLetter === "y") return `${string.slice(0, -1)}ies`;
  return `${string}s`;
};

export const greetings = (name) => {
  const hour = new Date().getHours();
  let greeting = "";
  if (hour < 12) {
    greeting += "Good Morning";
  } else if (hour < 18) {
    greeting += "Good Afternoon";
  } else {
    greeting += "Good Evening";
  }
  return greeting + (name ? `, ${name.trim()}!` : "!");
};

export const youtubePage = (influencer) =>
  influencer.youtube_page ? influencer.youtube_page.replace("/about", "") : "";

export const language = (code) => {
  if (code) {
    return isoLanguages[code.slice(0, 2)] || code;
  }
  return "";
};

export const isVip = (roles) => {
  const vipRoles = ["vip_brand_manager", "vip_influencer"];
  return vipRoles.some((role) => roles.includes(role));
};

const getPercentage = (num, denom) => Math.round((num / denom) * 100);

export const vyrillVerifiedPercentage = (videoCount) => {
  if (videoCount < 10) return 100;
  return videoCount ? getPercentage(10, videoCount) : 10;
};

export const getGamePercentage = (notes, videoCount) => {
  const processedVideos = notes.filter(
    (note) => note.are_products_verified && note.is_sponsored_verified
  );
  return videoCount
    ? getPercentage(processedVideos && processedVideos.length, videoCount)
    : 0;
};

export const convertSecToString = (timeInSec) => {
  let strTime = "";
  let seconds = timeInSec;
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    strTime += `${hours}h `;
    seconds -= hours * 3600;
  }

  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    strTime += `${minutes}m `;
    seconds -= minutes * 60;
  }

  return `${strTime || ""}${seconds || ""}s`;
};

export const calcAvg = (numerator, denominator) =>
  denominator ? Math.round((numerator || 0) / denominator) : 0;

export const percent = (count, total) =>
  `${Math.round((count / total) * 100) || 0}%`;

export const circlePercent = (count, total) =>
  Math.round(Math.sqrt(count / total) * 100);

export const filterNotes = (notes) => {
  const filteredNotes = notes.reduce((mostRecentNotes, note) => {
    if (note.vip_influencer_id) {
      if (mostRecentNotes[note.video_id]) {
        const currentNote = mostRecentNotes[note.video_id];
        if (currentNote.updated_at < note.updated_at) {
          mostRecentNotes[note.video_id] = note;
        }
      } else {
        mostRecentNotes[note.video_id] = note;
      }
    }
    return mostRecentNotes;
  }, {});
  return Object.values(filteredNotes);
};

export const getLicensingDuration = (numMonths) =>
  Object.keys(licensingDurations).find(
    (duration) => licensingDurations[duration] === numMonths
  ) || `${numMonths} months`;

export const getLicensingTableDuration = (numMonths) =>
  Object.keys(licensingTableDurations).find(
    (duration) => licensingTableDurations[duration] === numMonths
  ) || `${numMonths === null ? "Invalid" : numMonths}`;

export const getLicenseDuration = (note) =>
  note && note.licensing_duration
    ? getLicensingDuration(note.licensing_duration)
    : getLicensingDuration(0);

export const roundToTwo = (num) => Math.round(num * 100) / 100;

export const getLicensePrice = (note) =>
  note ? roundToTwo(Number(note.licensing_price)) : 0;

export const getLicenseFee = (price) =>
  price <= 0.99 ? 6 : roundToTwo(price * 0.2);

export const getLicenseTotalPrice = (note) => {
  const price = getLicensePrice(note);
  const fee = getLicenseFee(price);
  return roundToTwo(price + fee);
};

export const formatPrice = (num) => `$${Number(num).toFixed(2)}`;

export const getValidHrefUrl = (link) => {
  let url = link;
  if (!/^https?:\/\//i.test(url)) {
    url = `http://${url}`;
  }
  return url;
};

export const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const roundToTwoDecimal = (num) =>
  typeof num === "number" ? Number(num).toFixed(2) : "";

export const videoTopicsAndSubTopics = (topics, subTopics) => {
  topics = Array.isArray(topics) ? topics : [];
  subTopics = Array.isArray(subTopics) ? subTopics : [];
  const topicsAndSubTopics = topics.concat(subTopics);
  return topicsAndSubTopics
    .filter((value, index) => topicsAndSubTopics.indexOf(value) === index)
    .join(", ");
};

export const sizeInMB = (size) => {
  const sizeInMb = size ? (size / 1048576).toFixed(2) : "";
  return sizeInMb ? `${sizeInMb} MB` : "";
};

export const isOwnVideo = (
  video,
  videoAuthor,
  sessionInfluencerId,
  currentUserId
) => {
  const videoPlatform = video?.platform;
  if (videoAuthor) {
    if (videoPlatform === "Youtube") {
      return sessionInfluencerId
        ? videoAuthor._id === sessionInfluencerId
        : false;
    }
    if (videoPlatform === "Dropbox") {
      return videoAuthor.vip_influencer_id === currentUserId;
    }
    if (videoPlatform === "Tiktok") {
      return videoAuthor.vip_influencer_id === currentUserId ? true : false;
    }
  }
  return false;
};

export const arrayOptions = (objects) => {
  objects = Array.isArray(objects) ? objects : [];
  const arrayOption = objects.map((object) => object.name);
  return arrayOption || [];
};

export const addDefaultSrc = (
  event,
  src = require("@src/assets/images/vyrill/default.png").default
) => {
  event.target.onerror = null;
  event.target.src = src;
};

export const flattened = (arrayOfArrays) =>
  arrayOfArrays.reduce((acc, curVal) => acc.concat(curVal), []);

export const searchTranscript = (search, transcriptionText) => {
  const transcriptions = transcriptionText.split(".");
  return transcriptions.filter((sentence) =>
    sentence.toLowerCase().includes(search.toLowerCase())
  );
};

export const getFilterSearchTranscript = (search, transcriptionText) => {
  const limitChar = 250;
  const initialSentences = searchTranscript(search, transcriptionText);
  const flatText = flattened(initialSentences).join();
  if (flatText.length > limitChar) {
    const cutSentences = searchTranscript(search, flatText.slice(0, limitChar));
    return [...cutSentences, "See more in transcript"];
  }
  return initialSentences;
};

export const isEmpty = (obj) =>
  [Object, Array].includes((obj || {}).constructor) &&
  !Object.entries(obj || {}).length;

export const getCurrentBrandIfTryAndBuy = (currentBrand, isTryAndBuy) => {
  if (isTryAndBuy && !isEmpty(currentBrand)) {
    return {
      ...currentBrand,
      name: getCompanyNameIfTryAndBuy(currentBrand.name),
    };
  }
  return currentBrand;
};

export const getInitialSession = (session) => {
  const { currentUser, currentBrand } = session;
  const isTryAndBuy = currentUser && currentUser.try_and_buy;
  if (isTryAndBuy && !isEmpty(currentBrand)) {
    return {
      ...session,
      currentBrand: {
        ...currentBrand,
        name: getCompanyNameIfTryAndBuy(currentBrand.name),
      },
    };
  }
  return session;
};

export const convertStringToNumber = (str) => {
  if (typeof str === "number") {
    return str;
  }
  if (typeof str === "string") {
    return Number(str.replace(/[\\&/#,+()$~%'":*?<>{}]/g, ""));
  }
  return 0;
};

export const getAudioPercentages = (audioLabels) => {
  const audioClassification = {};
  const { toString } = Object.prototype;
  const keys = Object.keys(audioLabels);
  if (keys.length) {
    const arrayOfData = audioLabels[keys[0]];
    if (arrayOfData.length) {
      arrayOfData.forEach((element) => {
        const type = toString.call(element);
        if (type === "[object Object]") {
          Object.keys(element).forEach((classificationName) => {
            audioClassification[classificationName] = convertStringToNumber(
              element[classificationName]
            );
          });
        }
      });
    }
  }
  return audioClassification;
};

export const getAudioClassification = (audioLabels) => {
  if (!isEmpty(audioLabels.audio_classification)) {
    return audioLabels.audio_classification;
  }
  return getAudioPercentages(audioLabels);
};

export const getUnreadMessageCounts = (
  channels,
  lastSeenChannels,
  publisherId
) =>
  Object.keys(channels).reduce((counts, pubnubId) => {
    const channel = lastSeenChannels.find(
      ({ pubnub_id }) => pubnub_id === pubnubId
    );
    const lastSeen = channel ? channel.last_seen : 0;
    const messages = channels[pubnubId];

    const channelUnreadCount = messages.reduce(
      (sum, { message: { publisher }, timetoken }) =>
        publisher !== publisherId && timetoken > lastSeen ? sum + 1 : sum,
      0
    );

    counts[pubnubId] = channelUnreadCount;
    return counts;
  }, {});

export const getChannelsWithTimetokens = (
  currentChannels = [],
  lastSeenChannels = []
) => {
  const channels = [];
  const channelTimetokens = [];

  currentChannels.forEach((pubnubId) => {
    const channel = lastSeenChannels.find(
      ({ pubnub_id }) => pubnub_id === pubnubId
    );
    const lastSeen = channel ? channel.last_seen : 0;
    if (channel && channel._id) {
      channels.push(channel._id);
      channelTimetokens.push(lastSeen);
    }
  });

  return {
    channels,
    channelTimetokens,
  };
};

export const getUniqueBrandNames = (brands = []) => {
  const uniqueBrands = [];
  if (Array.isArray(brands)) {
    brands.forEach((brand) => {
      const cleanBrand = getCompanyNameIfTryAndBuy(brand);
      if (!uniqueBrands.includes(cleanBrand)) {
        uniqueBrands.push(cleanBrand);
      }
    });
  }
  return uniqueBrands;
};

const SI_POSTFIXES = ["", "K", "M", "G", "T", "P", "E"];

export const abbreviateNumber = (value, afterPoint = 1) => {
  const number = parseInt(value, 10);
  if (!number) return "0";
  const sign = number < 0 ? "-1" : "";
  const absNumber = Math.abs(number);
  const tier = (Math.log10(absNumber) / 3) | 0;
  // if zero, we don't need a prefix
  if (tier === 0) return `${absNumber}`;
  // get postfix and determine scale
  const postfix = SI_POSTFIXES[tier];
  const scale = Math.pow(10, tier * 3);
  // scale the number
  const scaled = absNumber / scale;
  const floored = Math.floor(scaled * 10) / 10;
  // format number and add postfix as suffix
  let str = floored.toFixed(afterPoint);
  // remove '.0' case
  str = /\.0$/.test(str) ? str.substr(0, str.length - 2) : str;
  return `${sign}${str}${postfix}`;
};

export const getCleanBrandNames = (data = {}) => {
  const { filters = {} } = data || {};
  if (!isEmpty(filters)) {
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "brand") {
        filters[key] = getUniqueBrandNames(value);
      } else {
        filters[key] = value;
      }
    });
  }

  return {
    ...data,
    filters,
  };
};

export const getDisplayValue = (num) => {
  if (Number.isInteger(num) || num === 0) {
    return num.toLocaleString();
  }
  return "N/A";
};

export const getStringDisplayValue = (str) => {
  if ((typeof str === "string" && str.length === 0) || str === null) {
    return "N/A";
  }
  return str;
};

export const isArray = (a) => Array.isArray(a);

export const isObject = (value) =>
  typeof value === "object" &&
  value !== null &&
  !isArray(value) &&
  !(value instanceof RegExp) &&
  !(value instanceof Error) &&
  !(value instanceof Date);

export const toCamel = (s) =>
  s.replace(/([-_][a-z])/gi, ($1) =>
    $1.toUpperCase().replace("-", "").replace("_", "")
  );

export const keysToCamelCase = (value) => {
  if (isObject(value)) {
    const n = {};
    Object.keys(value).forEach((k) => {
      n[toCamel(k)] = keysToCamelCase(value[k]);
    });
    return n;
  }
  if (isArray(value)) {
    return value.map((i) => keysToCamelCase(i));
  }
  return value;
};

export const timeDifference = (previous) => {
  const publishedAt = new Date(previous);
  const current = new Date();
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;
  const elapsed = current - publishedAt;

  if (elapsed < msPerMinute || elapsed < msPerHour || elapsed < msPerDay) {
    return "today";
  }
  if (elapsed < msPerMonth) {
    const countDays = Math.round(elapsed / msPerDay);
    return countDays === 1 ? `${countDays} day ago` : `${countDays} days ago`;
  }
  if (elapsed < msPerYear) {
    const countMonths = Math.round(elapsed / msPerMonth);
    return countMonths === 1
      ? `${countMonths} month ago`
      : `${countMonths} months ago`;
  }
  const countYears = Math.round(elapsed / msPerYear);
  if (countYears) {
    return countYears === 1
      ? `${countYears} year ago`
      : `${countYears} years ago`;
  }
};

export const filterSentencesBySearchText = (sentences, searchText) =>
  sentences.filter(({ positiveSentence, negativeSentence }) =>
    (positiveSentence || negativeSentence)
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

export const getDifferenceInMilliseconds = (latestDate, oldestDate) =>
  latestDate.getTime() - oldestDate.getTime();

export const getDifferenceInDays = (latestDate, oldestDate) =>
  getDifferenceInMilliseconds(latestDate, oldestDate) / (1000 * 3600 * 24);

export const getBrandName = (product, brand) => {
  if (product && product.manufacturerName) {
    return product.manufacturerName;
  }
  return brand.name;
};

export const discoverVideoOnOffIcon = (discover) => {
  if (discover === true) {
    return <i className="icon-product-on" />;
  }
  return <i className="icon-product-off" />;
};

export const updateTab = (tab, filters) => ({
  tab,
  page: 1,
  filters: cloneDeep(filters) || {},
});

export const tabDescriptions = (tab) => {
  if (tab === "Custom Report") {
    return "Overview of custom data from all video content for your brand & product(s).";
  } else if (tab === "Sentiment Report") {
    return "Top sentiment data from all the video content for your brand & product(s).";
  } else if (tab === "Demographic Report") {
    return "Top demographic data from all the video content for your brand & product(s).";
  } else if (tab === "Engagement Report") {
    return "Engagement report captures creator and video engagement over a period of time.";
  } else if (tab === "Speech Report") {
    return "Top key-phrases from all video content for your brand & product(s).";
  } else {
    return "";
  }
};

export const tabImagePath = (tab) => {
  if (tab === "Custom Report") {
    return "custom_report.png";
  } else if (tab === "Sentiment Report") {
    return "sentiment_report.png";
  } else if (tab === "Demographic Report") {
    return "demographic_report.png";
  } else if (tab === "Engagement Report") {
    return "product_engagement_report.png";
  } else if (tab === "Speech Report") {
    return "speech_report.jpg";
  } else {
    return "";
  }
};

const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getStartDate = (selectedDuration) => {
  switch (selectedDuration) {
    case "today":
      return formatDate(new Date());
    case "1d":
      const prevDay = new Date();
      prevDay.setDate(prevDay.getDate() - 1);
      return formatDate(prevDay);
    case "1w":
      const prevWeek = new Date();
      prevWeek.setDate(prevWeek.getDate() - 7);
      return formatDate(prevWeek);
    case "1m":
      const prevMonth = new Date();
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      return formatDate(prevMonth);
    case "3m":
      const prev3Month = new Date();
      prev3Month.setMonth(prev3Month.getMonth() - 3);
      return formatDate(prev3Month);
    case "6m":
      const prev6Month = new Date();
      prev6Month.setMonth(prev6Month.getMonth() - 6);
      return formatDate(prev6Month);
    case "ytd":
      const today = new Date();
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return formatDate(yearStart);
    case "1y":
      const prev1Year = new Date();
      prev1Year.setFullYear(prev1Year.getFullYear() - 1);
      return formatDate(prev1Year);
    default:
      return null;
  }
};

export const getDateString = (selectedRange) => {
  const today = new Date();
  const options = { year: "numeric", month: "short", day: "numeric" };

  if (selectedRange === "today") {
    const previousDay = new Date(today);
    previousDay.setDate(today.getDate() - 1);
    return `${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "1d") {
    const previousDay = new Date(today);
    previousDay.setDate(today.getDate() - 1);
    return `${previousDay.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "1w") {
    const previousWeekStart = new Date(today);
    previousWeekStart.setDate(today.getDate() - 7);
    return `${previousWeekStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "1m") {
    const previousMonthStart = new Date(today);
    previousMonthStart.setMonth(today.getMonth() - 1);
    return `${previousMonthStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "3m") {
    const previousMonthStart = new Date(today);
    previousMonthStart.setMonth(today.getMonth() - 3);
    return `${previousMonthStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "6m") {
    const previousMonthStart = new Date(today);
    previousMonthStart.setMonth(today.getMonth() - 6);
    return `${previousMonthStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "ytd") {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    return `${yearStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  if (selectedRange === "1y") {
    const previousYearStart = new Date(today);
    previousYearStart.setFullYear(today.getFullYear() - 1);
    return `${previousYearStart.toLocaleDateString(
      "en-US",
      options
    )} - ${today.toLocaleDateString("en-US", options)}`;
  }

  return "";
};

export const getDateStringSpeechReport = (selectedRange) => {
  const today = new Date();
  const options = { month: "short", day: "numeric" };

  const getFormattedDateRange = (startDate, endDate) => {
    const formattedStartDate = startDate.toLocaleDateString("en-US", options);
    const formattedEndDate = endDate.toLocaleDateString("en-US", options);
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    if (startYear === endYear) {
      return `${formattedStartDate} - ${formattedEndDate}, ${endYear}`;
    } else {
      return `${formattedStartDate}, ${startYear} - ${formattedEndDate}, ${endYear}`;
    }
  };

  if (selectedRange === "1w") {
    const previousWeekStart = new Date(today);
    previousWeekStart.setDate(today.getDate() - 7);
    return getFormattedDateRange(previousWeekStart, today);
  }

  if (selectedRange === "1m") {
    const previousMonthStart = new Date(today);
    previousMonthStart.setMonth(today.getMonth() - 1);
    return getFormattedDateRange(previousMonthStart, today);
  }

  if (
    selectedRange === "3m" ||
    selectedRange === "6m" ||
    selectedRange === "1y"
  ) {
    const startDate = new Date(today);
    startDate.setMonth(
      today.getMonth() -
        (selectedRange === "3m" ? 3 : selectedRange === "6m" ? 6 : 12)
    );
    return getFormattedDateRange(startDate, today);
  }

  if (selectedRange === "ytd") {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    return getFormattedDateRange(yearStart, today);
  }

  return "";
};

export const sortArray = (arr) => {
  const sortedArr = [...arr];
  sortedArr.sort((a, b) => a.score - b.score);
  return sortedArr;
};

export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const formattedTime = `${hours}:${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
  return formattedTime;
};

export const splitDateRange = (dateRange) => {
  const dates = dateRange.split(" - ");
  if (dates.length > 0) {
    const startDate = moment(dates[0], "MMM D, YYYY");
    const endDate = moment(dates[1], "MMM D, YYYY");
    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  }

  return null;
};

export const compareDate = (compareWith, inputDate) => {
  const inputDateTime = new Date(inputDate);
  const comparisonDate = new Date(compareWith);

  // Compare dates
  if (inputDateTime < comparisonDate) {
    return "2022-01-01";
  } else {
    // Convert Date object back to string in "YYYY-MM-DD" format
    const formattedDate = inputDateTime.toISOString().split("T")[0];
    return formattedDate;
  }
};

export const formatDateToYYYYMMDD = (inputDate) => {
  const dateObject = new Date(inputDate);
  const year = dateObject.getFullYear();
  const month = (dateObject.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based, so add 1
  const day = dateObject.getDate().toString().padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const fillMissingDates = (data, startDate) => {
  const currentDate = new Date();
  startDate = new Date(startDate);

  // Create an empty object to store data by date
  const dateMap = {};

  // Populate the dateMap with the existing data
  for (const item of data) {
    const itemDate = new Date(item.date);
    const itemDateString = itemDate.toISOString().split("T")[0];
    dateMap[itemDateString] = item;
  }

  // Create an array to store the final result
  const filledData = [];

  // Loop from the start date to the current date
  let currentDateIterator = new Date(startDate);
  while (currentDateIterator <= currentDate) {
    const currentDateStr = currentDateIterator.toISOString().split("T")[0];

    // If the date exists in the dateMap, use the existing data
    if (dateMap[currentDateStr]) {
      filledData.push(dateMap[currentDateStr]);
    } else {
      // If the date is missing, create a placeholder with default values
      filledData.push({
        comment_count: 0,
        date: currentDateStr,
        likes: 0,
        sentiment_score: 0,
        total_engagement: 0,
        view_count: 0,
        youtube_dislike_count: 0,
        videoCount: 0,
      });
    }

    // Move to the next date
    currentDateIterator.setDate(currentDateIterator.getDate() + 1);
  }

  return filledData;
};

export const coverTimeStampToHHMMSS = (totalSeconds) => {
  if (isNaN(totalSeconds)) {
    return totalSeconds;
  }
  totalSeconds = Math.round(totalSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Ensure that hours, minutes, and seconds are always two digits
  const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const removeQueryParams = (url) => {
  const parsedUrl = new URL(url);
  parsedUrl.search = "";
  return parsedUrl.toString();
};

export const getCurrentTimezone = () => {
  const currentTimezone = momentTz.tz.guess();

  // Get the current time in the guessed timezone
  const currentTime = momentTz().tz(currentTimezone);

  // Format the time to display as GMT offset with timezone name
  const formattedTime =
    "GMT" + currentTime.format("Z") + " (" + currentTime.format("zz") + ")";

  return formattedTime;
};

export const isCampaignVideo = (upload_method) => {
  return upload_method === "Clip-Up" || upload_method === "Clip-up";
};

export const downloadAndPrepareVideos = async (videoUrls, zip) => {
  for (let url of videoUrls) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ${url}`);
    const videoBlob = await response.blob();
    const fileName = `Video_${videoUrls.indexOf(url) + 1}.mp4`;
    zip.file(fileName, videoBlob);
  }
  return zip;
};

export const getVideoUrls = (data) =>
  data.map((item) => item.video?.video_url).filter((url) => url);

export const getVideoUrlsById = (data, ids) => {
  const matchedVideoUrls = data
    ?.filter(
      (item) =>
        item.video &&
        item?.video?._id &&
        item?.video?.video_url &&
        ids.includes(item.video._id)
    )
    .map((item) => item.video.video_url);

  // Return the array of matched video URLs
  return matchedVideoUrls;
};

export const getDirectory = async () => {
  try {
    return await window.showDirectoryPicker();
  } catch (error) {
    console.error("Error in selecting directory:", error);
    alert("Failed to open directory picker. Please try again.");
    return null;
  }
};
